import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Users, Globe, Package, Building2, Factory, ArrowRight } from 'lucide-react';
import { heroSlides, companyStats, productGroups, blogPosts, features, contactInfo, references } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const getIcon = (iconName) => {
    const icons = {
      calendar: Calendar,
      users: Users,
      globe: Globe,
      package: Package,
      building: Building2,
      factory: Factory
    };
    const Icon = icons[iconName] || Package;
    return <Icon className="w-12 h-12" />;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Slider */}
      <section className="relative h-screen overflow-hidden">
        {/* Bottom Wave - More Curved Design with Transparency */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-10 pointer-events-none">
          <svg className="relative block w-full h-40" viewBox="0 0 1200 120" preserveAspectRatio="none" style={{ transform: 'rotate(180deg)', opacity: 0.95 }}>
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".15" fill="#ffffff"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".35" fill="#ffffff"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" opacity=".7" fill="#ffffff"></path>
          </svg>
        </div>
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${slide.image}?w=1920&q=80)`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
            </div>
            <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
              <div className="max-w-2xl">
                <h1 className="text-5xl lg:text-6xl font-bold mb-4 text-white">
                  {slide.title} <span className="text-[#E31E24]">{slide.subtitle}</span>
                </h1>
                <p className="text-xl text-white mb-8">{slide.description}</p>
                {slide.buttonText && (
                  <Button 
                    asChild
                    className="bg-[#E31E24] hover:bg-[#C01A1F] text-white px-8 py-6 text-lg rounded-lg"
                  >
                    <Link to={slide.buttonLink}>{slide.buttonText}</Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-gray-800/50 hover:bg-gray-800/70 text-white flex items-center justify-center transition-all z-10"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-gray-800/50 hover:bg-gray-800/70 text-white flex items-center justify-center transition-all z-10"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-[#E31E24] w-8' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Company Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">
            Geçmişin gücü <span className="text-[#E31E24]">yenilikçi ürünlerimizin küresel pazarda öncü olmasını sağlıyor.</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mt-16">
            {companyStats.map((stat) => (
              <div key={stat.id} className="text-center">
                <div className="flex justify-center mb-4 text-gray-700">
                  {getIcon(stat.icon)}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-blue-400/20 blur-3xl" />
              <img
                src="https://images.unsplash.com/photo-1633533452148-a9657d2c9a5f?w=800&q=80"
                alt="Sürdürülebilirlik"
                className="relative shadow-2xl w-full"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Sürdürülebilirliği <span className="text-[#E31E24]">Ambalaj Sürecinde Seviyoruz!</span>
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                Tüm müşteri hizmetleri döngüsü boyunca sürdürülebilirliği hedefliyoruz. Çevre dostu üretim süreçlerimiz ve geri dönüştürülebilir malzemelerimiz ile geleceğe yatırım yapıyoruz.
              </p>
              <Button asChild className="bg-[#E31E24] hover:bg-[#C01A1F] text-white px-8 py-6 rounded-lg">
                <Link to="/surdurulebilirlik">DETAY</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Product Groups Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Ürün Grupları</h2>
            <p className="text-lg text-gray-700">E-ticaret ihtiyaçlarınıza uygun çözümler. Kargo poşeti, jelatin, fermuarlı torba, jelatin vs.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {productGroups.map((group) => (
              <Card key={group.id} className="group hover:shadow-2xl transition-all duration-300 overflow-hidden border-0 rounded-none">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={`${group.image}?w=600&q=80`}
                    alt={group.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2 group-hover:text-[#E31E24] transition-colors">{group.name}</h3>
                  <p className="text-gray-600 mb-4">{group.description}</p>
                  <Link 
                    to={group.link} 
                    className="inline-flex items-center text-[#E31E24] font-semibold group-hover:gap-2 transition-all"
                  >
                    Detaylı İncele <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <div key={feature.id} className={`flex gap-6 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-[#E31E24]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* References Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Referanslarımız</h2>
            <p className="text-lg text-gray-700">Bizimle çalışan değerli iş ortaklarımız</p>
          </div>
          <div className="relative overflow-hidden">
            {/* Carousel Container */}
            <div className="flex gap-8 animate-scroll">
              {/* First set */}
              {references.map((ref) => (
                <div 
                  key={`ref-1-${ref.id}`}
                  className="flex-shrink-0 w-48 h-24 bg-white rounded-lg shadow-md flex items-center justify-center p-4 hover:shadow-xl transition-shadow"
                >
                  <img 
                    src={ref.image} 
                    alt={`Referans ${ref.id}`}
                    className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all"
                  />
                </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {references.map((ref) => (
                <div 
                  key={`ref-2-${ref.id}`}
                  className="flex-shrink-0 w-48 h-24 bg-white rounded-lg shadow-md flex items-center justify-center p-4 hover:shadow-xl transition-shadow"
                >
                  <img 
                    src={ref.image} 
                    alt={`Referans ${ref.id}`}
                    className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Blog</h2>
            <p className="text-lg text-gray-700">Güncel haberler ve gelişmelerden haberdar olun!</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Card key={post.id} className="group hover:shadow-2xl transition-all duration-300 overflow-hidden border-0 rounded-none">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={`${post.image}?w=600&q=80`}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="text-sm text-gray-500 mb-2">{post.date}</div>
                  <h3 className="text-xl font-bold mb-3 group-hover:text-[#E31E24] transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                  <Link 
                    to={post.link} 
                    className="inline-flex items-center text-[#E31E24] font-semibold hover:gap-2 transition-all"
                  >
                    DEVAMINI OKU <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
